import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { LogOut, Tag, MapPin, Clock, Percent, ChevronRight, Rocket, Search, X } from 'lucide-react';
import EventShareControl from '../components/EventShareControl';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Offers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 195 — Client-side search across the already-fetched offer list.
  // Mirrors the Discover + Events UX.
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/offers`)
      .then(res => setOffers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (offer) => {
    axios.post(`${API}/api/posts/${offer.id}/click`, {}).catch(() => {});
    // Open the dedicated, shareable offer page (deep links) — matches Events.
    navigate(`/offers/${offer.id}`);
  };

  const shareOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://find-2-day.com';

  // Track view impressions for visible offers
  useEffect(() => {
    offers.forEach(o => {
      axios.post(`${API}/api/posts/${o.id}/view`, {}).catch(() => {});
    });
  }, [offers]);

  const daysLeft = (dateStr) => {
    const d = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return d > 0 ? d : 0;
  };

  // Iter 195 — Filter the loaded offers on the fly. Searches across title,
  // description, business name and city.
  const normalisedSearch = searchTerm.trim().toLowerCase();
  const visibleOffers = normalisedSearch
    ? offers.filter((o) => {
        const haystack = [o.title, o.description, o.businessName, o.businessCity]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalisedSearch);
      })
    : offers;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="offers-title">
              <Tag className="h-6 w-6" />
              {t('nav.offers', 'Offers')}
            </h1>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              {user ? (
                <button onClick={() => { logout(); navigate('/'); }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="logout-button">
                  <LogOut className="h-4 w-4" /><span className="hidden sm:inline">{t('logout', 'Logout')}</span>
                </button>
              ) : (
                <button onClick={() => navigate('/signin')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="signin-button">
                  <span>{t('signIn', 'Sign In')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Iter 195 — Search row, same UX as Discover and Events. */}
          <div className="mt-3 relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-orange-400"
              aria-hidden="true"
            />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder={t('searchOffersPlaceholder', 'Search offers...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              className="w-full pl-10 pr-10 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white outline-none"
              data-testid="offers-search-input"
              aria-label={t('searchOffersPlaceholder', 'Search offers...')}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid="offers-search-clear-btn"
                aria-label={t('clearSearch', 'Clear search')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl h-28 animate-pulse" />)}
          </div>
        ) : offers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Tag className="h-14 w-14 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('noOffersYet', 'No Special Offers Yet')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('offersWillAppear', 'Check back soon for amazing deals from local businesses!')}</p>
            <button onClick={() => navigate('/discover')}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
              {t('browseBusinesses', 'Browse Businesses')}
            </button>
          </div>
        ) : visibleOffers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="offers-no-search-results">
            <Tag className="h-14 w-14 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('noOffersForSearch', 'No offers match your search')}</h2>
            <p className="text-sm text-gray-500 mb-5">&ldquo;{searchTerm}&rdquo;</p>
            <button onClick={() => setSearchTerm('')}
              className="px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
              data-testid="offers-clear-search-cta">
              {t('clearSearch', 'Clear search')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">{visibleOffers.length} {t('activeOffers', 'active offers')}</p>
            {visibleOffers.map((offer) => (
              <div key={offer.id} onClick={() => handleClick(offer)}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleClick(offer); }}
                className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group cursor-pointer"
                data-testid={`offer-card-${offer.id}`}>
                <div className="p-4 flex items-start gap-3">
                  {/* Discount badge — always shown so the % stays visible */}
                  {offer.discountPercentage ? (
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-lg font-bold text-orange-600 leading-none">{offer.discountPercentage}%</span>
                      <span className="text-[10px] text-orange-500 font-medium">OFF</span>
                    </div>
                  ) : (
                    <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Percent className="h-6 w-6 text-orange-500" />
                    </div>
                  )}
                  {/* Offer image — only when uploaded */}
                  {offer.image && (
                    <img
                      src={offer.image}
                      alt={offer.title}
                      loading="lazy"
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-orange-100"
                      data-testid={`offer-card-image-${offer.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate text-sm group-hover:text-orange-600 transition-colors">{offer.title}</h3>
                      {(offer.boosted || offer.boostPost) && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full flex-shrink-0" data-testid={`sponsored-badge-${offer.id}`}>
                          <Rocket className="h-2.5 w-2.5" />{t('sponsored', 'Sponsored')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{offer.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {offer.businessName && (
                        <span className="flex items-center gap-1 font-medium text-gray-600">{offer.businessName}</span>
                      )}
                      {offer.businessCity && (
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{offer.businessCity}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {daysLeft(offer.validUntil) === 0
                          ? t('expirestoday', 'Expires today')
                          : `${daysLeft(offer.validUntil)} ${t('daysLeft', 'days left')}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-shrink-0 mt-1" onClick={(e) => e.stopPropagation()}>
                    <EventShareControl
                      url={`${shareOrigin}/api/share/offers/${offer.id}`}
                      title={`${offer.title} – ${offer.businessName || ''}`.trim()}
                      text={offer.description || ''}
                      testId={`offer-${offer.id}`}
                    />
                    <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-orange-400 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Offers;
